import { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Camera, Star, X } from "lucide-react";
import { toast } from "sonner";
import { SectionHeading } from "./PriceMenu";

const BASE = process.env.REACT_APP_BACKEND_URL;
const API = `${BASE}/api`;
const inputCls =
  "w-full rounded-sm border border-[#D4AF37]/25 bg-[#FFFFFF] px-4 py-3 text-sm text-[#1B3A6B] placeholder:text-[#9AA3B8] focus:border-[#D4AF37] focus:outline-none";

const Stars = ({ value, onChange, size = 15 }) => (
  <div className="flex gap-1" aria-label={`${value} star rating`}>
    {[1, 2, 3, 4, 5].map((n) => (
      <button
        key={n}
        type="button"
        disabled={!onChange}
        data-testid={onChange ? `review-star-${n}` : undefined}
        onClick={() => onChange?.(n)}
        className={
          onChange ? "transition-transform hover:scale-110" : "cursor-default"
        }
        aria-label={`${n} star${n > 1 ? "s" : ""}`}
      >
        <Star
          size={size}
          className={
            n <= value ? "fill-[#D4AF37] text-[#D4AF37]" : "text-[#D4AF37]/35"
          }
        />
      </button>
    ))}
  </div>
);

const ReviewCard = ({ r, i }) => (
  <motion.figure
    data-testid={`review-card-${i}`}
    initial={{ opacity: 0, y: 28 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.6, delay: (i % 3) * 0.12 }}
    className="flex flex-col overflow-hidden rounded-sm border border-[#D4AF37]/20 bg-[#FFFFFF]/60 backdrop-blur-xl"
  >
    {r.photo_url && (
      <img
        src={r.photo_url}
        alt={`Cake shared by ${r.name}`}
        data-testid={`review-photo-${i}`}
        className="h-56 w-full object-cover"
      />
    )}
    <div className="flex flex-1 flex-col p-8">
      <Stars value={r.rating} />
      <blockquote className="mt-5 flex-1 font-display text-lg italic leading-relaxed text-[#1B3A6B]">
        “{r.comment}”
      </blockquote>
      <figcaption className="mt-6 border-t border-[#D4AF37]/15 pt-5">
        <p className="text-sm font-semibold tracking-wide text-[#8A6D1F]">
          {r.name}
        </p>
        {r.occasion && (
          <p className="mt-1 text-xs uppercase tracking-[0.18em] text-[#67718A]">
            {r.occasion}
          </p>
        )}
      </figcaption>
    </div>
  </motion.figure>
);

const ReviewForm = ({ onSubmitted }) => {
  const [form, setForm] = useState({
    name: "",
    occasion: "",
    rating: 5,
    comment: "",
  });
  const [photo, setPhoto] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [sending, setSending] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const uploadPhoto = async (file) => {
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await axios.post(`${API}/reviews/upload`, fd);
      setPhoto(`${BASE}/api/files/${data.path}`);
    } catch (err) {
      toast.error(
        err?.response?.data?.detail ||
          "Could not upload photo. Please use a JPG or PNG under 5 MB.",
      );
    } finally {
      setUploading(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/reviews`, {
        ...form,
        occasion: form.occasion || null,
        photo_url: photo,
      });
      toast.success(
        "Thank you! Your review will appear once Tarah has approved it.",
      );
      setForm({ name: "", occasion: "", rating: 5, comment: "" });
      setPhoto(null);
      onSubmitted?.();
    } catch {
      toast.error("Sorry, we couldn't send your review. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <form
      onSubmit={submit}
      data-testid="review-form"
      className="rounded-sm border border-[#D4AF37]/25 bg-[#FFFFFF]/80 p-8 backdrop-blur-xl"
    >
      <p className="font-display text-xl text-[#8A6D1F]">Leave a review</p>
      <p className="mt-1 text-sm text-[#67718A]">
        Ordered from Tarah? We would love to hear how it went.
      </p>
      <div className="mt-6 grid gap-4 sm:grid-cols-2">
        <input
          data-testid="review-name-input"
          required
          maxLength={80}
          className={inputCls}
          placeholder="Your name"
          value={form.name}
          onChange={set("name")}
        />
        <input
          data-testid="review-occasion-input"
          maxLength={80}
          className={inputCls}
          placeholder="Occasion (e.g. Birthday cake)"
          value={form.occasion}
          onChange={set("occasion")}
        />
      </div>
      <div className="mt-4 flex items-center gap-4">
        <span className="text-xs uppercase tracking-[0.2em] text-[#67718A]">
          Rating
        </span>
        <Stars
          value={form.rating}
          onChange={(n) => setForm((f) => ({ ...f, rating: n }))}
          size={20}
        />
      </div>
      <textarea
        data-testid="review-comment-input"
        required
        minLength={3}
        maxLength={600}
        rows={4}
        className={`${inputCls} mt-4 resize-none`}
        placeholder="Tell us about your cake"
        value={form.comment}
        onChange={set("comment")}
      />
      <div
        className="mt-4 rounded-sm border border-dashed border-[#D4AF37]/40 bg-[#FBF7EE]/50 p-4"
        data-testid="review-photo-section"
      >
        <p className="text-xs uppercase tracking-[0.2em] text-[#9A7B24]">
          Share your pretty moments with us
        </p>
        <p className="mt-1 text-xs text-[#67718A]">
          Add a photo of your cake or celebration (optional — JPG or PNG, under
          5 MB).
        </p>
        {photo ? (
          <div className="relative mt-3 inline-block">
            <img
              src={photo}
              alt="Your uploaded photo"
              data-testid="review-photo-preview"
              className="h-28 w-40 rounded-sm object-cover"
            />
            <button
              type="button"
              data-testid="review-photo-remove"
              onClick={() => setPhoto(null)}
              aria-label="Remove photo"
              className="absolute -right-2 -top-2 rounded-full bg-[#1B3A6B] p-1 text-white transition-colors hover:bg-[#8A6D1F]"
            >
              <X size={12} />
            </button>
          </div>
        ) : (
          <label
            data-testid="review-photo-upload"
            className="mt-3 inline-flex cursor-pointer items-center gap-2 rounded-full border border-[#D4AF37]/50 px-4 py-2 text-xs font-medium text-[#8A6D1F] transition-colors hover:bg-[#D4AF37] hover:text-[#1B3A6B]"
          >
            <Camera size={14} /> {uploading ? "Uploading…" : "Upload a photo"}
            <input
              type="file"
              accept="image/*"
              className="hidden"
              disabled={uploading}
              onChange={(e) => uploadPhoto(e.target.files[0])}
            />
          </label>
        )}
      </div>
      <button
        data-testid="review-submit-button"
        type="submit"
        disabled={sending || uploading}
        className="mt-6 rounded-full bg-[#D4AF37] px-8 py-3 text-sm font-semibold text-[#1B3A6B] transition-all duration-300 hover:bg-[#8A6D1F] disabled:opacity-60"
      >
        {sending ? "Sending…" : "Submit Review"}
      </button>
    </form>
  );
};

export const Reviews = () => {
  const [reviews, setReviews] = useState([]);

  useEffect(() => {
  axios
    .get(`${API}/reviews`)
    .then((r) => {
      const data = Array.isArray(r.data)
        ? r.data
        : Array.isArray(r.data?.reviews)
          ? r.data.reviews
          : [];

      setReviews(data);
    })
    .catch(() => {
      setReviews([]);
    });
  }, []);

  return (
    <section
      id="reviews"
      data-testid="reviews-section"
      className="border-y border-[#D4AF37]/10 bg-[#F7F2E8]/60 py-24 lg:py-32"
    >
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <SectionHeading
          eyebrow="Customer Reviews"
          title="What Our Customers Say"
          testId="reviews-heading"
        />

        {reviews.length === 0 ? (
          <p
            data-testid="reviews-empty"
            className="mx-auto mt-12 max-w-xl text-center font-display text-lg italic text-[#67718A]"
          >
            We are a brand-new studio — be the first to share your experience.
          </p>
        ) : (
          <div
            className="mt-14 grid gap-8 md:grid-cols-3"
            data-testid="reviews-grid"
          >
            {reviews.map((r, i) => (
              <ReviewCard key={r.id} r={r} i={i} />
            ))}
          </div>
        )}

        <div className="mx-auto mt-14 max-w-2xl">
          <ReviewForm />
        </div>
      </div>
    </section>
  );
};
